import CreatableSortableSelect from 'Shared/components/CreatableSortableSelect'
import { useAct } from 'FlowBuilder/store'
import './css/DonationAmounts.scoped.scss'
import getNavigatorLocale from 'FlowBuilder/lib/localeHelper'

export default function DonationAmounts({ actionId }) {
  const action = useSel(s => s.actions.entities[s.local.selectedActionId])
  const config = action.config || { amounts: {}, defaultAmounts: {} }
  const currencies = config.currencies || window.ENV.SETTINGS.default_currencies
  const act = useAct()

  function setAmountsForCurrency(currency, amounts) {
    act.actions.setAmountsForCurrency({ actionId, currency, amounts })
  }

  function setDefaultAmountForCurrency(currency, defaultAmount) {
    act.actions.setDefaultAmountForCurrency({ actionId, currency, defaultAmount })
  }

  return (
    <div className="donation-amounts-container">
      <h3 className="donation-amounts-heading">Preset Amounts</h3>
      {currencies.map(currency => {
        const setAmounts = amounts => setAmountsForCurrency(currency, amounts)
        const setDefaultAmount = defaultAmount => setDefaultAmountForCurrency(currency, defaultAmount)
        const amounts = config.amounts[currency] || []
        const defaultAmount = config.defaultAmounts?.[currency] || ''

        return (
          <div key={currency} className="currency-row">
            <span className="currency-label">{currency}</span>
            <div className="amounts-and-default-wrapper">
              <div className="select-wrapper">
                <CurrencyAmounts {...{ amounts, setAmounts, currency }} />
              </div>
              <div className="default-selector-wrapper">
                <DefaultAmountSelector
                  amounts={amounts}
                  defaultAmount={defaultAmount}
                  setDefaultAmount={setDefaultAmount}
                  currency={currency}
                />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  );
}

function DefaultAmountSelector({ amounts, defaultAmount, setDefaultAmount, currency }) {
  const formatter = new Intl.NumberFormat(getNavigatorLocale(), {
    style: 'currency',
    currency: currency.toUpperCase()
  })

  if (amounts.length === 0) {
    return (
      <div className="default-amount-placeholder">
        Add amounts first
      </div>
    )
  }

  return (
    <select
      className="default-amount-select"
      value={defaultAmount}
      onChange={e => setDefaultAmount(e.target.value)}
    >
      <option value="">Select default</option>
      {amounts.map((amount, index) => (
        <option key={index} value={amount}>
          {formatter.format(amount / 100)} (default)
        </option>
      ))}
    </select>
  )
}

function CurrencyAmounts({ amounts, setAmounts, currency }) {
  const minAmount = 1;
  const maxAmount = 100000;
  const formatter = new Intl.NumberFormat(getNavigatorLocale(), {
    style: 'currency',
    currency: currency.toUpperCase()
  })

  const optionsValue = amounts.map(a => ({ label: formatter.format(a / 100), value: a.toString() }))

  // Validation function for numbers only
  const isValidNewOption = (inputValue, selectValue) => {
    if (!inputValue) return false;

    const cleanValue = inputValue.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(cleanValue);

    if (isNaN(numValue)) return false;
    if (numValue < minAmount || numValue > maxAmount) return false;

    const valueInPence = Math.round(numValue * 100);
    const exists = selectValue.some(option =>
      Math.round(parseFloat(option.value)) === valueInPence
    );

    return !exists;
  };

  // Format the create label
  const formatCreateLabel = (inputValue) => {
    const cleanValue = inputValue.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(cleanValue);

    if (isNaN(numValue)) {
      return `"${inputValue}" is not a valid amount`;
    }

    if (numValue < minAmount || numValue > maxAmount) {
      return `Amount must be between ${formatter.format(minAmount)} and ${formatter.format(maxAmount)}`;
    }

    return `Add ${formatter.format(numValue)}`;
  };

  // Custom create option function
  const onCreateOption = (inputValue) => {
    const cleanValue = inputValue.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(cleanValue);

    if (!isNaN(numValue) && numValue >= minAmount && numValue <= maxAmount) {
      const valueInPence = Math.round(numValue * 100);
      return {
        label: formatter.format(numValue),
        value: valueInPence.toString()
      };
    }
    return null;
  };

  const handleAmountsChange = (options) => {
    const parsedAmounts = options.map(option => {
      if (option.__isNew__) {
        const cleanValue = option.value.replace(/[^0-9.]/g, '');
        const numValue = parseFloat(cleanValue);
        if (!isNaN(numValue)) {
          const valueInPence = Math.round(numValue * 100);
          return valueInPence;
        }
      }
      return parseFloat(option.value);
    });
    setAmounts(parsedAmounts);
  };

  // Filter input to max 2dp numbers only
  const onInputChange = (newValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      // Remove any non-numeric characters except decimal point
      let cleaned = newValue.replace(/[^0-9.]/g, '');

      // Ensure only one decimal point
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }

      // Limit to 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        cleaned = parts[0] + '.' + parts[1].substring(0, 2);
      }

      return cleaned;
    }
    return newValue;
  };

  return (
    <CreatableSortableSelect
      value={optionsValue}
      onChange={handleAmountsChange}
      placeholder="Enter amount (numbers only)..."
      isValidNewOption={isValidNewOption}
      formatCreateLabel={formatCreateLabel}
      onCreateOption={onCreateOption}
      onInputChange={onInputChange}
      hideDropdownIndicator={true}
      closeMenuOnCreate={true}
    />
  )
}
